import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePreferences } from '../contexts/PreferencesContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Book, ArrowRight, Globe, BookOpen, Brain, MessageSquare, GraduationCap } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const { setScriptMode } = usePreferences();
  const { language, setLanguage, t } = useLanguage();
  const [selectedMode, setSelectedMode] = useState(null);
  const [counters, setCounters] = useState({
    vocabulary_count: "+1000",
    quiz_count: "+500",
    situations_count: "+100",
    lessons_count: "+50"
  });

  useEffect(() => {
    // Fetch landing counters
    axios.get(`${API}/settings/landing-counters`)
      .then(res => setCounters(res.data))
      .catch(() => {}); // Use defaults on error
  }, []);

  const handleScriptChoice = (mode) => {
    setScriptMode(mode);
    setSelectedMode(mode);
  };

  const handleContinue = () => {
    if (selectedMode) {
      // GUEST MODE: Go directly to dashboard without authentication
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-sand">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1729456228671-b6af2f2b4d11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtYXJyYWtlY2glMjByZWQlMjBjaXR5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MzIzMzY2OXww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-4">
        {/* Language Switcher */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-end mb-2"
        >
          <div className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 shadow-md">
            <Globe className="w-3 h-3 text-charcoal/60" />
            <button
              onClick={() => setLanguage('fr')}
              className={`px-2 py-0.5 rounded-full text-xs font-nunito transition-colors ${
                language === 'fr' 
                  ? 'bg-primary text-white' 
                  : 'text-charcoal/70 hover:bg-sand'
              }`}
            >
              FR
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-2 py-0.5 rounded-full text-xs font-nunito transition-colors ${
                language === 'en' 
                  ? 'bg-primary text-white' 
                  : 'text-charcoal/70 hover:bg-sand'
              }`}
            >
              EN
            </button>
          </div>
        </motion.div>

        {/* Header - Logo + Title + Slogan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-4"
        >
          {/* Logo */}
          <div className="w-16 h-16 mx-auto mb-2">
            <img src="/logo.svg" alt="MoroccoTalk Logo" className="w-full h-full" />
          </div>
          <h1 className="font-playfair text-3xl sm:text-5xl md:text-7xl font-bold text-primary mb-2" data-testid="landing-title">
            MoroccoTalk
          </h1>
          <p className="font-nunito text-sm sm:text-lg md:text-xl text-charcoal/80">
            {language === 'en' 
              ? 'Learn Darija simply through image and sound' 
              : "Apprenez la Darija simplement par l'image et le son"}
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          {/* Images - Moved up, reduced height */}
          <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-4">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative overflow-hidden rounded-xl shadow-lg"
            >
              <img 
                src="https://images.unsplash.com/photo-1763146741798-1d289241eaf8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxtYXJyYWtlY2glMjByZWQlMjBjaXR5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MzIzMzY2OXww&ixlib=rb-4.1.0&q=85"
                alt="Moroccan Architecture"
                className="w-full h-28 sm:h-40 object-cover"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative overflow-hidden rounded-xl shadow-lg"
            >
              <img 
                src="https://images.unsplash.com/photo-1678129456841-47b1aca89e60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxtb3JvY2NhbiUyMG1pbnQlMjB0ZWElMjBwb3VyaW5nfGVufDB8fHx8MTc3MzIzMzY4Mnww&ixlib=rb-4.1.0&q=85"
                alt="Moroccan Tea Culture"
                className="w-full h-28 sm:h-40 object-cover"
              />
            </motion.div>
          </div>

          {/* Script Selection Card - Reduced padding */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 border-2 border-stone-200"
          >
            <h2 className="font-playfair text-xl sm:text-2xl font-bold text-center text-charcoal mb-4">
              {language === 'en' 
                ? 'Choose your writing mode' 
                : "Choisissez votre mode d'écriture"}
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
              <button
                onClick={() => handleScriptChoice('phonetic')}
                data-testid="phonetic-mode-btn"
                className={`tile-button font-nunito font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-xl active:shadow-none hover:shadow-tile-hover transition-all ${
                  selectedMode === 'phonetic'
                    ? 'bg-primary text-white shadow-[0_4px_0_#9a360b] ring-4 ring-primary/30'
                    : 'bg-primary text-white shadow-[0_4px_0_#9a360b]'
                }`}
              >
                <Book className="w-6 h-6 mx-auto mb-2" />
                <div className="text-base sm:text-lg mb-1">
                  {language === 'en' ? 'Phonetic Mode' : 'Mode Phonétique'}
                </div>
                <div className="text-xs opacity-90">
                  {language === 'en' 
                    ? "For those who don't know Arabic" 
                    : "Pour ceux qui ne connaissent pas l'arabe"}
                </div>
              </button>
              
              <button
                onClick={() => handleScriptChoice('arabic')}
                data-testid="arabic-mode-btn"
                className={`tile-button font-nunito font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-xl active:shadow-none hover:shadow-tile-hover transition-all ${
                  selectedMode === 'arabic'
                    ? 'bg-secondary text-white shadow-[0_4px_0_#004d40] ring-4 ring-secondary/30'
                    : 'bg-secondary text-white shadow-[0_4px_0_#004d40]'
                }`}
              >
                <div className="font-amiri text-2xl mb-2">عربي</div>
                <div className="text-base sm:text-lg mb-1">
                  {language === 'en' ? 'Arabic Mode' : 'Mode Arabe'}
                </div>
                <div className="text-xs opacity-90">
                  {language === 'en' 
                    ? 'For those who know the Arabic alphabet' 
                    : "Pour ceux qui connaissent l'alphabet arabe"}
                </div>
              </button>
            </div>

            {/* Continue Button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: selectedMode ? 1 : 0.5 }}
              transition={{ duration: 0.3 }}
            >
              <button
                onClick={handleContinue}
                disabled={!selectedMode}
                data-testid="continue-btn"
                className={`w-full tile-button font-nunito font-bold py-3 px-6 rounded-xl text-base flex items-center justify-center gap-2 ${
                  selectedMode
                    ? 'bg-oasis text-white shadow-[0_4px_0_#004d40] hover:shadow-lg'
                    : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                }`}
              >
                {language === 'en' ? 'Continue' : 'Continuer'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </motion.div>

          {/* Dynamic Counters - Moved below mode selection, reduced size */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="grid grid-cols-4 gap-2 sm:gap-4 mt-4"
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-xl px-2 sm:px-3 py-2 shadow-md border border-primary/20">
              <div className="flex items-center justify-center gap-1">
                <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="font-playfair text-lg sm:text-xl font-bold text-primary">{counters.vocabulary_count}</span>
              </div>
              <p className="font-nunito text-[10px] sm:text-xs text-charcoal/70 text-center">
                {language === 'en' ? 'Words' : 'Mots'}
              </p>
            </div>
            
            <div className="bg-white/95 backdrop-blur-sm rounded-xl px-2 sm:px-3 py-2 shadow-md border border-secondary/20">
              <div className="flex items-center justify-center gap-1">
                <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-secondary" />
                <span className="font-playfair text-lg sm:text-xl font-bold text-secondary">{counters.quiz_count}</span>
              </div>
              <p className="font-nunito text-[10px] sm:text-xs text-charcoal/70 text-center">
                Quiz
              </p>
            </div>
            
            <div className="bg-white/95 backdrop-blur-sm rounded-xl px-2 sm:px-3 py-2 shadow-md border border-accent/20">
              <div className="flex items-center justify-center gap-1">
                <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-accent" />
                <span className="font-playfair text-lg sm:text-xl font-bold text-accent">{counters.situations_count}</span>
              </div>
              <p className="font-nunito text-[10px] sm:text-xs text-charcoal/70 text-center">
                Situations
              </p>
            </div>
            
            <div className="bg-white/95 backdrop-blur-sm rounded-xl px-2 sm:px-3 py-2 shadow-md border border-orange-400/20">
              <div className="flex items-center justify-center gap-1">
                <GraduationCap className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500" />
                <span className="font-playfair text-lg sm:text-xl font-bold text-orange-500">{counters.lessons_count}</span>
              </div>
              <p className="font-nunito text-[10px] sm:text-xs text-charcoal/70 text-center">
                {language === 'en' ? 'Lessons' : 'Leçons'}
              </p>
            </div>
          </motion.div>

          {/* Features Preview - Hidden on mobile, visible on larger screens */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-4 hidden sm:grid grid-cols-3 gap-3"
          >
            <div className="text-center p-3 bg-white/50 rounded-xl">
              <div className="text-2xl mb-1">📚</div>
              <p className="font-nunito text-xs text-charcoal/70">
                {language === 'en' ? 'Vocabulary' : 'Vocabulaire'}
              </p>
            </div>
            <div className="text-center p-3 bg-white/50 rounded-xl">
              <div className="text-2xl mb-1">💬</div>
              <p className="font-nunito text-xs text-charcoal/70">
                {language === 'en' ? 'Conversations' : 'Conversations'}
              </p>
            </div>
            <div className="text-center p-3 bg-white/50 rounded-xl">
              <div className="text-2xl mb-1">🤖</div>
              <p className="font-nunito text-xs text-charcoal/70">
                {language === 'en' ? 'AI Chatbot' : 'Chatbot IA'}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
